import { useGetList, useGetMany, useRedirect, useTranslate } from "ra-core";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { isDealOpen } from "../deals/dealStatus";
import { todayKey } from "../deals/dealUtils";
import { NEXT_STEP_TYPES } from "../deals/nextStep";
import type { Deal, WorkItem } from "../types";

// Terskel for «trenger oppfølging»: eldste kontaktpunkt er over 90 dager
// gammelt (eller mangler helt).
const FOLLOW_UP_THRESHOLD_DAYS = 90;

// Rad fra viewet company_last_contact (kun aktive kunder). last_contact_sort
// er -infinity for kunder uten kontaktpunkter, så stigende sortering legger
// «Aldri kontaktet» øverst.
type CompanyLastContact = {
  id: number;
  company_id: number;
  name: string;
  last_contact_at: string | null;
  last_contact_type:
    | "company_note"
    | "contact_note"
    | "email_in"
    | "email_out"
    | "email"
    | "task"
    | null;
  last_contact_sort: string;
};

const TYPE_LABELS: Record<string, { key: string; fallback: string }> = {
  company_note: {
    key: "crm.dashboard.radar.type.company_note",
    fallback: "Notat",
  },
  contact_note: {
    key: "crm.dashboard.radar.type.contact_note",
    fallback: "Kontaktnotat",
  },
  email_in: {
    key: "crm.dashboard.radar.type.email_in",
    fallback: "E-post fra kunden",
  },
  email_out: {
    key: "crm.dashboard.radar.type.email_out",
    fallback: "E-post sendt",
  },
  email: { key: "crm.dashboard.radar.type.email", fallback: "E-post" },
  task: { key: "crm.dashboard.radar.type.task", fallback: "Oppgave" },
};

// Terskeldato som ren dato (uten klokkeslett). VIKTIG: verdien inngår i
// react-querys nøkkel – et ustabilt tidsstempel (ms-presisjon) gir ny nøkkel
// per render og dermed evig refetch-løkke. Dato-oppløsning er stabil.
const thresholdIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - FOLLOW_UP_THRESHOLD_DAYS);
  return d.toISOString().slice(0, 10);
};

// Oppfølgingsradar (PR-4): kundene som har gått lengst uten kontakt, eldst
// først. Kunder helt uten kontaktpunkter vises øverst som «Aldri kontaktet».
export const NeedsFollowUp = () => {
  const translate = useTranslate();
  const redirect = useRedirect();

  const {
    data: companies = [],
    isPending: companiesPending,
    isError: companiesError,
    refetch: refetchCompanies,
  } = useGetList<CompanyLastContact>("company_last_contact", {
    filter: { "last_contact_sort@lt": thresholdIso() },
    sort: { field: "last_contact_sort", order: "ASC" },
    pagination: { page: 1, perPage: 8 },
  });

  // Muligheter med forfalt neste steg (PR-6). Neste steg er oppgaver koblet
  // til muligheten (work_items.deal_id), ikke lenger deals.next_step_date.
  // Lukkede og arkiverte muligheter siles bort i klienten, så vi henter litt
  // flere enn vi viser.
  const today = todayKey();
  const {
    data: overdueTasks = [],
    isPending: tasksPending,
    isError: tasksError,
    refetch: refetchTasks,
  } = useGetList<WorkItem>("work_items", {
    filter: {
      "completed_at@is": null,
      "deal_id@not.is": null,
      "due_date@lt": today,
    },
    sort: { field: "due_date", order: "ASC" },
    pagination: { page: 1, perPage: 50 },
  });
  // Revisit, anmeldelse og fakturering er ikke neste steg.
  const stepTasks = overdueTasks.filter(
    (t) =>
      t.deal_id != null &&
      !t.completed_at &&
      !!t.due_date &&
      t.due_date < today &&
      (t.task_type == null || NEXT_STEP_TYPES.includes(t.task_type)),
  );
  const dealIds = [...new Set(stepTasks.map((t) => String(t.deal_id)))];
  const {
    data: deals = [],
    isPending: dealsFetching,
    isError: dealsFetchError,
    refetch: refetchDeals,
  } = useGetMany<Deal>(
    "deals",
    { ids: dealIds },
    { enabled: dealIds.length > 0 },
  );
  const dealById = new Map(deals.map((d) => [String(d.id), d]));
  // Én rad per mulighet: det eldste forfalte steget (lista er sortert).
  const seen = new Set<string>();
  const overdueDeals = stepTasks
    .flatMap((task) => {
      const deal = dealById.get(String(task.deal_id));
      const key = String(task.deal_id);
      if (!deal || deal.archived_at || !isDealOpen(deal) || seen.has(key)) {
        return [];
      }
      seen.add(key);
      return [{ deal, task }];
    })
    .slice(0, 5);
  const dealsPending = tasksPending || (dealIds.length > 0 && dealsFetching);
  const dealsError = tasksError || (dealIds.length > 0 && dealsFetchError);
  const hasError = companiesError || dealsError;
  const isLoading = companiesPending || dealsPending;
  const retry = () => {
    void Promise.all([
      refetchCompanies(),
      refetchTasks(),
      ...(dealIds.length > 0 ? [refetchDeals()] : []),
    ]);
  };

  // «E-post · 12.3.2026» – eller «Aldri kontaktet» uten kontaktpunkter.
  const lastContactLabel = (company: CompanyLastContact) => {
    if (company.last_contact_at == null) {
      return translate("crm.dashboard.radar.never", { _: "Aldri kontaktet" });
    }
    const type = TYPE_LABELS[company.last_contact_type ?? ""];
    return [
      type ? translate(type.key, { _: type.fallback }) : null,
      new Date(company.last_contact_at).toLocaleDateString("nb-NO"),
    ]
      .filter(Boolean)
      .join(" · ");
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <h2 className="section-title">
          {translate("crm.dashboard.radar.title", { _: "Trenger oppfølging" })}
        </h2>
      </div>
      <Card className="py-0">
        {hasError ? (
          <div
            role="alert"
            className="flex flex-col items-start gap-2 p-4 text-sm"
          >
            <p>Kunne ikke hente oppfølgingene. Prøv igjen.</p>
            <button
              type="button"
              onClick={retry}
              className="font-medium text-primary underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Prøv igjen
            </button>
          </div>
        ) : isLoading ? (
          <div aria-label="Laster oppfølginger" className="space-y-3 p-4">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        ) : companies.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            {translate("crm.dashboard.radar.empty", {
              _: `Alle kunder er kontaktet de siste ${FOLLOW_UP_THRESHOLD_DAYS} dagene.`,
            })}
          </p>
        ) : (
          <div className="divide-y divide-border">
            {companies.map((company) => (
              <button
                key={company.id}
                type="button"
                onClick={() =>
                  redirect(`/companies/${company.company_id}/show`)
                }
                className="flex min-h-12 w-full items-center justify-between gap-3 px-4 py-2 text-left transition-colors hover:bg-muted outline-none focus-visible:bg-muted"
              >
                <span className="block min-w-0 truncate text-sm">
                  {company.name}
                </span>
                <span className="shrink-0 text-xs font-medium text-muted-foreground">
                  {lastContactLabel(company)}
                </span>
              </button>
            ))}
          </div>
        )}
        {/* Forfalte neste steg fra Salg-pipelinen (PR-6) – egen seksjon. */}
        {!hasError && !isLoading && overdueDeals.length > 0 && (
          <div className="border-t">
            <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {translate("crm.dashboard.radar.overdue_deals", {
                _: "Forfalte neste steg (muligheter)",
              })}
            </p>
            <div className="divide-y divide-border">
              {overdueDeals.map(({ deal, task }) => (
                <button
                  key={deal.id}
                  type="button"
                  onClick={() => redirect(`/deals/${deal.id}/show`)}
                  className="flex min-h-12 w-full items-center justify-between gap-3 px-4 py-2 text-left transition-colors hover:bg-muted outline-none focus-visible:bg-muted"
                >
                  <span className="block min-w-0 truncate text-sm">
                    {deal.name}
                    {task.title ? (
                      <span className="text-muted-foreground">
                        {" "}
                        · {task.title}
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-xs font-medium text-red-600 dark:text-red-500">
                    {task.due_date &&
                      new Date(`${task.due_date}T12:00:00`).toLocaleDateString(
                        "nb-NO",
                      )}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
